/**
 * A map's history (docs/plans/2026-10-21-map-history.md): its versions and
 * the changes of the last week, kept by the server, which reads none of it.
 * This module asks the server (handlers/sync/history.rs), decrypts with the
 * map's keys (the current one, then older ones after a key change), rebuilds
 * the map at a moment, compares two moments item by item, and restores: a
 * copy as a new map, items brought back, or (the owner) the whole map.
 *
 * Interfaces use it through `engine.history` (engine/index.js).
 */
import * as Y from "yjs";
import { VaultStorage } from "./crypto/vault-storage.js";
import { getCurrentUser } from "./account.js";
import { decryptPayload } from "./sync/ws-encryption.js";
import { readMap, readEditSessions, LOCAL_FIELDS } from "./sync/map-format.js";
import { sessionSignatureValid, pinnedSigningKey } from "./crypto/signing-key.js";
import { ensureNotesSerializer, fragmentToMarkdown } from "./sync/notes-serializer-facade.js";
import * as CRDTBridge from "./sync/crdt-bridge.js";
import * as StorageHealth from "./sync/storage-health.js";
import { isActiveShareReadOnly } from "./sync/share-state.js";
import { on, Events } from "./core/events.js";

// ==================== The server ====================

async function request(mapId, path = "", { method = "GET", body } = {}) {
  const response = await fetch(`/api/docs/${encodeURIComponent(mapId)}/history${path}`, {
    method,
    credentials: "include",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    let message = "";
    try { message = (await response.json())?.error || ""; } catch { /* no body */ }
    const error = new Error(message || `History unavailable (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// ==================== Keys and people ====================

/** The map's keys, newest first: the current one, then those it had before. */
async function mapKeys(mapId) {
  const keys = [];
  const add = async (raw) => {
    if (!raw) return;
    try {
      const key = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["decrypt"]);
      keys.push({ raw, key });
    } catch { raw.fill(0); }
  };
  await add(await VaultStorage.getDocDEK(mapId).catch(() => null));
  const previous = [...(VaultStorage.getDoc(mapId)?.previousWrappedDEKs || [])].reverse();
  for (const wrapped of previous) await add(await VaultStorage.unwrapAccountDEK(wrapped).catch(() => null));
  return keys;
}

function forget(keys) {
  for (const k of keys) k.raw.fill(0);
}

// The people of a map as the server listed them last (history), per map:
// account id → { name, signingKey (base64, pinned on this device) }.
const peopleOf = new Map();

/** Who can be named in a map's history: its members, past and present. */
function remember(mapId, people) {
  const me = getCurrentUser();
  const out = new Map();
  for (const p of people || []) {
    out.set(p.user_id, {
      name: p.username || null,
      signingKey: me?.user_id ? pinnedSigningKey(me.user_id, p.user_id, p.signing_key || null) : null,
    });
  }
  if (me?.user_id) out.set(me.user_id, { ...(out.get(me.user_id) || {}), name: me.username || null, me: true });
  peopleOf.set(mapId, out);
  return out;
}

async function membersOf(mapId) {
  return peopleOf.get(mapId) || remember(mapId, (await request(mapId)).people);
}

/**
 * Who made each change (docs/architecture/map-history.md, "Who did what"),
 * from three sources that must agree:
 *  - the server: the editing session each change arrived on (`session`);
 *  - the map: signed entries "session S (Yjs client C) is account A",
 *    checked with A's signing key (`keyOf`: the key pinned on this device);
 *  - the change itself: the Yjs clients that wrote it (`clients`).
 * Per change: `{ user, status }`; `status` "ok" (named), "older" (no session:
 * an app or server older than this), "unnamed" (no valid entry), "mismatch"
 * (the three disagree: never shown under anyone's name).
 */
export function attribute(changes, entries, keyOf) {
  const bySession = new Map();
  for (const entry of entries) {
    const key = keyOf(entry.user);
    if (!key || !sessionSignatureValid(entry, key)) continue;
    if (!bySession.has(entry.session)) bySession.set(entry.session, []);
    bySession.get(entry.session).push(entry);
  }
  return changes.map((change) => {
    if (change.session === null || change.session === undefined) return { user: null, status: "older" };
    const named = bySession.get(change.session) || [];
    const users = unique(named.map((entry) => entry.user));
    if (!users.length) return { user: null, status: "unnamed" };
    const clients = new Set(named.map((entry) => entry.client));
    if (users.length > 1 || change.clients.some((client) => !clients.has(client))) return { user: null, status: "mismatch" };
    return { user: users[0], status: "ok" };
  });
}

/** The key sittings group by: an account, or what could not be named. */
const whoKey = ({ user, status }) => (status === "ok" ? user : `#${status}`);

/**
 * A person as the history shows them: an account (`status` "ok"), or what
 * could not be named ("older", "unnamed", "mismatch": see `attribute`).
 */
function person(who, named) {
  if (who.startsWith("#")) return { userId: null, name: null, status: who.slice(1) };
  const { signingKey, ...known } = named.get(who) || {};
  return { userId: who, name: null, ...known, status: "ok" };
}

const unique = (list) => [...new Set(list)];

/** The Yjs clients whose writes `update` holds. */
export const clientsIn = (update) => [...Y.parseUpdateMeta(update).to.keys()];

/**
 * The Yjs clients whose writes `update` adds beyond `seen` (client → the
 * clock already reached; moved on). A change that only sends again writes
 * already stored (an app's reconnect step resending another person's work
 * it thought missing) adds none, so it is not weighed against its session:
 * it was shown as "records don't match" (simulation, 2026-10-26). A server
 * filing a change under another session can only refile one already
 * stored (it cannot write content), which adds nothing; a new change still
 * has to match its session.
 * @param {Uint8Array} update
 * @param {Map<number, number>} seen
 * @returns {number[]}
 */
export function clientsAdding(update, seen) {
  const added = [];
  for (const [client, clock] of Y.parseUpdateMeta(update).to) {
    if (clock > (seen.get(client) || 0)) {
      added.push(client);
      seen.set(client, clock);
    }
  }
  return added;
}

const SITTING_GAP = 10 * 60 * 1000;
const SITTING_JOIN = 60 * 1000;
const SITTING_MAX = 2 * 60 * 60 * 1000;

/**
 * Changes, in order (`{ version, id, at, users }`, `users` the accounts that
 * made it, null for an unnamed session), grouped into sittings: changes less
 * than 10 minutes apart, at most 2 hours long; another person joins a sitting
 * only by changing the map within a minute of its last change (live
 * collaboration). A change without any session (a deletion from an older
 * app) joins the sitting it falls in.
 * @returns {Array<{from, to, start, end, count, users: Array<string|null>}>}
 */
export function sittingsOf(changes) {
  const out = [];
  for (const c of changes) {
    const at = new Date(c.at).getTime();
    const last = out.at(-1);
    const joins = last && at - last.endTime < SITTING_GAP && at - last.startTime < SITTING_MAX
      && (c.users.length === 0 || c.users.some((u) => last.users.has(u)) || at - last.endTime <= SITTING_JOIN);
    const here = { version: c.version, change: c.id };
    if (joins) {
      last.to = here;
      last.end = c.at;
      last.endTime = Math.max(last.endTime, at);
      last.count++;
    } else {
      out.push({ from: here, to: here, start: c.at, end: c.at, startTime: at, endTime: at, count: 1, users: new Set() });
    }
    for (const u of c.users) out.at(-1).users.add(u);
  }
  return out.map(({ startTime, endTime, users, ...s }) => ({ ...s, users: [...users] }));
}

// ==================== Reading ====================

/**
 * The map's history: its versions (oldest first) and the sittings of the
 * last week's changes, with who made them (the map says which account each
 * editing session was; the server keeps no author).
 * @returns {Promise<Object>} role, isOwner, maxVersions, historyBytes,
 *   capBytes, visibleFrom, current, versions, sittings; each sitting's
 *   `people`: [{ userId, name, me, older }] (name null: someone this account
 *   cannot name, such as a former member). Who changed the map between two
 *   older versions comes with `changes`.
 */
export async function list(mapId) {
  // A version can go between the list and its reading (another device's
  // compaction thinned it, the owner deleted it): read everything again.
  for (let attempt = 1; ; attempt++) {
    try {
      return await listOnce(mapId);
    } catch (error) {
      if (error.status !== 404 || attempt >= 3) throw error;
    }
  }
}

async function listOnce(mapId) {
  const data = await request(mapId);
  const versions = data.versions.map((v) => ({
    id: v.id, at: v.at, endedAt: v.ended_at, reason: v.reason, hasBase: v.has_base,
    changesKept: v.changes_kept, changes: v.changes, bytes: v.bytes,
    sessions: v.edit_sessions || [], unknownSession: !!v.unknown_session,
  }));
  const named = remember(mapId, data.people);
  // The changes kept (the last week's) and the current ones, decrypted; the
  // map as it is now holds every session entry.
  const parts = [];
  for (const v of versions.filter((x) => x.changesKept)) parts.push({ version: v.id, content: await readVersion(mapId, v.id) });
  parts.push({ version: null, content: await readVersion(mapId, "current") });
  const now = new Y.Doc();
  let entries = [];
  try {
    const current = parts.at(-1).content;
    if (current.base) Y.applyUpdate(now, current.base);
    for (const c of current.changes) Y.applyUpdate(now, c.update);
    entries = readEditSessions(now);
  } finally {
    now.destroy();
  }
  // What each change adds, from its version's base on (clientsAdding).
  const raw = parts.flatMap(({ version, content }) => {
    const seen = content.base ? new Map(Y.parseUpdateMeta(content.base).to) : new Map();
    return content.changes.map((c) => ({
      version, id: c.id, at: c.at, session: c.session, clients: clientsAdding(c.update, seen),
    }));
  });
  const who = attribute(raw, entries, (user) => named.get(user)?.signingKey || null);
  const changes = raw.map((c, i) => ({ version: c.version, id: c.id, at: c.at, users: [whoKey(who[i])] }));
  return {
    role: data.role,
    isOwner: data.role === "owner",
    maxVersions: data.max_versions,
    historyBytes: data.history_bytes,
    capBytes: data.cap_bytes,
    visibleFrom: data.visible_from,
    current: { at: data.current.at, changes: data.current.changes },
    versions,
    sittings: sittingsOf(changes).map(({ users, ...s }) => ({ ...s, people: users.map((u) => person(u, named)) })),
  };
}

// Versions decrypted lately (their content, like the open map's, stays in
// this page's memory only), forgotten at sign-in and sign-out.
const cache = new Map();
const CACHE_SIZE = 6;
for (const event of [Events.AUTH_LOGIN, Events.AUTH_LOGOUT]) on(event, () => cache.clear());

/**
 * One version's base and kept changes, decrypted (`current`: the map's
 * current base and changes). Payloads no key opens are counted, not used.
 * @param {string} mapId
 * @param {number|"current"} version
 * @returns {Promise<{id: number|null, base: Uint8Array|null,
 *   changes: Array<{id: number, at: string, update: Uint8Array}>, unreadable: number}>}
 */
export async function readVersion(mapId, version) {
  const name = `${mapId}:${version}`;
  if (version !== "current" && cache.has(name)) return cache.get(name);
  const data = await request(mapId, `/versions/${version}`);
  const keys = await mapKeys(mapId);
  let unreadable = 0;
  let first = 0; // the key that opened the last payload is tried first
  const open = async (payload) => {
    for (let k = 0; k < keys.length; k++) {
      const i = (first + k) % keys.length;
      try {
        const bytes = await decryptPayload(payload, keys[i].key, mapId);
        first = i;
        return bytes;
      } catch { /* the next key */ }
    }
    unreadable++;
    return null;
  };
  try {
    const content = {
      id: data.id,
      base: data.base ? await open(data.base) : null,
      changes: [],
      unreadable: 0,
    };
    for (const change of data.changes) {
      const update = await open(change.delta);
      if (update) content.changes.push({ id: change.id, at: change.at, session: change.edit_session ?? null, update });
    }
    content.unreadable = unreadable;
    if (data.base && !content.base) content.baseUnreadable = true;
    if (version !== "current") {
      cache.set(name, content);
      if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);
    }
    return content;
  } finally {
    forget(keys);
  }
}

/**
 * The map at a moment: `ref.version` (a version id, or null: the current
 * base) with its changes up to `ref.change` (included; undefined: all of
 * them; null: none, the base alone). `before`: up to the change before it.
 * @returns {Promise<Y.Doc>} the caller destroys it
 */
export async function moment(mapId, { version = null, change, before = false } = {}) {
  const content = await readVersion(mapId, version ?? "current");
  if (content.baseUnreadable) {
    const error = new Error("This version cannot be read with this account's keys");
    error.code = "unreadable";
    throw error;
  }
  const doc = new Y.Doc();
  if (content.base) Y.applyUpdate(doc, content.base);
  if (change === null) return doc;
  for (const c of content.changes) {
    if (c.id === change && before) break;
    Y.applyUpdate(doc, c.update);
    if (c.id === change) break;
  }
  return doc;
}

/**
 * Who changed the map from `a` to `b` (a later moment), when the changes
 * themselves are gone (older versions): the Yjs clients that wrote anything
 * in between (their clock moved), named by the map's checked entries, and
 * checked against the sessions the server recorded for that span
 * (`sessions`, when given): an entry for another session is a "mismatch".
 * @returns {Array<{user: string|null, status: string}>}
 */
export function writersBetween(a, b, keyOf, sessions = null) {
  const before = Y.decodeStateVector(Y.encodeStateVector(a));
  const after = Y.decodeStateVector(Y.encodeStateVector(b));
  const clients = [...after].filter(([client, clock]) => clock > (before.get(client) || 0)).map(([client]) => client);
  const entries = [...readEditSessions(a), ...readEditSessions(b)]
    .filter((entry) => { const key = keyOf(entry.user); return key && sessionSignatureValid(entry, key); });
  const allowed = sessions ? new Set(sessions) : null;
  const out = new Map();
  for (const client of clients) {
    const named = entries.filter((entry) => entry.client === client);
    const users = unique(named.map((entry) => entry.user));
    let who;
    if (!named.length) who = { user: null, status: "unnamed" };
    else if (users.length > 1 || (allowed && !named.some((entry) => allowed.has(entry.session)))) who = { user: null, status: "mismatch" };
    else who = { user: users[0], status: "ok" };
    out.set(whoKey(who), who);
  }
  return [...out.values()];
}

/**
 * What changed between two moments (see `moment`), item by item (`compare`),
 * and who changed the map in between.
 * @returns {Promise<{changes: Array<Object>, before: Object, after: Object, people: Array<Object>}>}
 *   before/after: the items of each moment (itemsOf), for showing an item
 */
export async function changesBetween(mapId, from, to, { sessions = null } = {}) {
  const a = await moment(mapId, from);
  const b = await moment(mapId, to);
  try {
    const before = await itemsOf(a);
    const after = await itemsOf(b);
    const named = await membersOf(mapId);
    const people = writersBetween(a, b, (user) => named.get(user)?.signingKey || null, sessions)
      .map((who) => person(whoKey(who), named));
    return { changes: compare(before, after), before, after, people };
  } finally {
    a.destroy();
    b.destroy();
  }
}

// ==================== Comparing ====================

/** Not content: where the item is drawn, sizes, who edited last. */
const IGNORED = new Set([
  "id", "children", "childIds", "parentId", "order", "_lastEditedBy", "_lastEditedAt", "createdAt",
  "x", "y", ...LOCAL_FIELDS,
]);

/**
 * A map's items, readable: id → { id, name, parentId, order, fields, notes
 * (Markdown), notesKey (to compare) }, in the order the map shows them, and
 * its tag definitions.
 * @param {Y.Doc} doc
 */
export async function itemsOf(doc) {
  await ensureNotesSerializer();
  const { rootId, flat, children } = readMap(doc);
  const rich = doc.getMap("metadata").get("notesFormat") === "yxml-v1";
  const items = new Map();
  const visit = (id, parentId) => {
    const node = flat.get(id);
    if (!node) return;
    let notes = typeof node.notes === "string" ? node.notes : "";
    let notesKey = notes;
    const name = "notes-" + id;
    if (rich && doc.share.has(name)) {
      const fragment = doc.getXmlFragment(name);
      if (fragment.length > 0) {
        notesKey = fragment.toString();
        try { notes = fragmentToMarkdown(fragment); } catch { notes = ""; }
      }
    }
    const fields = {};
    for (const [field, value] of Object.entries(node)) {
      if (!IGNORED.has(field) && field !== "notes" && value !== null && value !== undefined && value !== "") fields[field] = value;
    }
    items.set(id, { id, name: node.name || "", parentId, order: node.order ?? null, fields, notes, notesKey });
    for (const child of children.get(id) || []) visit(child, id);
  };
  if (rootId) visit(rootId, null);
  return { rootId, items, tags: doc.getMap("metadata").get("tagDefinitions") || [] };
}

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * What changed from `before` to `after` (both from itemsOf), in the order the
 * map shows the items: created, deleted, modified (fields: name, notes,
 * parent, position, and every other field, with their values before and
 * after). Tag ids are given with the tag names of either moment.
 * @returns {Array<{id: string, kind: "created"|"deleted"|"modified", name: string,
 *   path: string[], fields: Array<{field: string, before: *, after: *}>}>}
 */
export function compare(before, after) {
  const tagNames = new Map([...before.tags, ...after.tags].map((t) => [t.id, t.name]));
  // A node's tags: { tagId, setAt } (older maps: the id alone).
  const named = (field, value) => (field === "tags" && Array.isArray(value)
    ? value.map((tag) => {
      const id = typeof tag === "string" ? tag : tag?.tagId;
      return tagNames.get(id) || id;
    })
    : value);
  const pathOf = (items, id) => {
    const names = [];
    for (let p = items.get(id)?.parentId; p; p = items.get(p)?.parentId) names.unshift(items.get(p)?.name || "");
    return names;
  };
  const out = [];
  for (const [id, b] of after.items) {
    const a = before.items.get(id);
    if (!a) {
      out.push({ id, kind: "created", name: b.name, path: pathOf(after.items, id), fields: [] });
      continue;
    }
    const fields = [];
    if (a.name !== b.name) fields.push({ field: "name", before: a.name, after: b.name });
    if (a.notesKey !== b.notesKey) fields.push({ field: "notes", before: a.notes, after: b.notes });
    if (a.parentId !== b.parentId) {
      fields.push({ field: "parent", before: before.items.get(a.parentId)?.name ?? null, after: after.items.get(b.parentId)?.name ?? null });
    } else if (a.order !== b.order && a.order !== null && b.order !== null) {
      fields.push({ field: "position", before: null, after: null });
    }
    for (const field of new Set([...Object.keys(a.fields), ...Object.keys(b.fields)])) {
      if (field === "name") continue;
      if (!sameValue(a.fields[field], b.fields[field])) {
        fields.push({ field, before: named(field, a.fields[field] ?? null), after: named(field, b.fields[field] ?? null) });
      }
    }
    if (fields.length) out.push({ id, kind: "modified", name: b.name, path: pathOf(after.items, id), fields });
  }
  for (const [id, a] of before.items) {
    if (!after.items.has(id)) out.push({ id, kind: "deleted", name: a.name, path: pathOf(before.items, id), fields: [] });
  }
  return out;
}

// ==================== Restoring ====================

/** This account owns the map (its own maps have no role in the vault). */
export function isOwner(mapId) {
  const role = VaultStorage.getDoc(mapId)?.role;
  return !role || role === "owner";
}

function openMapManager(mapId) {
  if (CRDTBridge.getActiveDocId() !== mapId) throw new Error("Open this map to restore it");
  const manager = CRDTBridge.getDocManager();
  if (!manager) throw new Error("Open this map to restore it");
  return manager;
}

/**
 * Bring items back into the open map as `version` had them (any member who
 * can edit): deleted ones come back with their ids, changed ones as they
 * were. One change, one Ctrl+Z step.
 * @param {string} mapId
 * @param {Y.Doc} version
 * @param {string[]} ids
 */
export function bringBack(mapId, version, ids) {
  const manager = openMapManager(mapId);
  if (isActiveShareReadOnly()) throw new Error("Read-only map");
  StorageHealth.assertHealthy();
  return manager.restoreFrom(version, { ids });
}

/**
 * The owner's full revert: the open map made as `version` had it (items,
 * notes, tag definitions), as one change of the owner's; the map as it was
 * before stays in the history. The journal, routines and Today list stay as
 * they are.
 */
export async function revert(mapId, version) {
  const manager = openMapManager(mapId);
  if (!isOwner(mapId)) throw new Error("Only the owner can revert this map");
  StorageHealth.assertHealthy();
  return manager.restoreFrom(version, { ids: null });
}

/**
 * A new map holding `version` (the map as it was then), in the same folder.
 * Files the version shows that were deleted since cannot come back: the copy
 * reports them.
 */
export async function restoreAsNewMap(mapId, version, { title } = {}) {
  const { copyMap } = await import("./services/map-copy.js");
  return copyMap(VaultStorage, mapId, { title, state: Y.encodeStateAsUpdate(version) });
}

// ==================== The owner's settings ====================

/** Delete one version (its changes join the version before it). */
export const deleteVersion = (mapId, version) => request(mapId, `/versions/${version}`, { method: "DELETE" });

/** Delete the map's whole history (the map stays as it is). */
export const deleteAll = (mapId) => {
  for (const name of [...cache.keys()]) if (name.startsWith(`${mapId}:`)) cache.delete(name);
  return request(mapId, "", { method: "DELETE" });
};

/** The maximum number of versions to keep (null: the schedule only; 0: none). */
export const setMaxVersions = (mapId, maxVersions) =>
  request(mapId, "/settings", { method: "PUT", body: { max_versions: maxVersions } });
